// Pure logic for tracking a single Connect attempt. Inputs are timestamped
// WiFiStatus events from ble-core; outputs are the transitions list and a
// derived result. No React, no BLE — testable in isolation.

import type { WiFiStatus } from '@soluu/ble-core';
import type { AttemptAnomaly, AttemptResult, Transition } from './types.js';

/**
 * SPEC §"Open Questions" #3: default time-to-fail for an attempt. Bumped
 * from 30 s to 60 s in 0007 to match the production mobile app's 30 s + 30 s
 * staged budget (see docs/reference/soluu_wifi_credentials_bloc.dart) and
 * cover the worst-case observed retry-then-recover times we've seen in the
 * field. Tunable per attempt if a scenario needs more or less headroom.
 */
export const DEFAULT_ATTEMPT_TIMEOUT_MS = 60_000;

/**
 * Append a transition for this status event, but only if the label
 * differs from the most recent transition. The harness logs *changes*,
 * not every notification: hardware sometimes fires repeats, and a
 * 60 s attempt of NOT_CONNECTED → NOT_CONNECTED → NOT_CONNECTED would
 * drown the actual signal.
 */
export function appendTransition(
  transitions: readonly Transition[],
  status: WiFiStatus,
  startedAtMs: number,
): readonly Transition[] {
  const last = transitions[transitions.length - 1];
  if (last && last.label === status.label) {
    return transitions;
  }
  const tMs = Math.max(0, status.timestamp - startedAtMs);
  return [...transitions, { tMs, label: status.label, raw: status.raw }];
}

export interface DerivedResult {
  readonly result: AttemptResult;
  readonly failureReason: string | null;
  readonly totalDurationMs: number;
  /** First time the device reported CONNECTED, or null if never reached. */
  readonly tConnectedMs: number | null;
  /** First time the device reported ON_INTERNET, or null if never reached. */
  readonly tOnInternetMs: number | null;
  /** Unexpected behaviors observed during the attempt. */
  readonly anomalies: readonly AttemptAnomaly[];
}

export interface DeriveOptions {
  readonly nowMs: number;
  readonly startedAtMs: number;
  readonly timeoutMs?: number;
}

/**
 * Derive the attempt result from the recorded transitions.
 *
 * Terminal labels:
 *   - ON_INTERNET → pass. The only true success: the device confirmed
 *     reachability through §217's ping endpoint.
 *   - ON_NETWORK → fail. Per SPEC §Non-Goals, ON_NETWORK means the device
 *     is already enrolled to an owner; not a passing test of *our* WiFi.
 *
 * **CANNOT_CONNECT is NOT terminal here.** Per the SPEC table its meaning is
 * "Attempted and failed", but firmware in the field has been observed to
 * emit it transiently and then proceed to CONNECTED → ON_INTERNET without
 * the harness writing anything new. Per session 0007 user direction: that's
 * a firmware behavior worth capturing, not silently masking. We continue
 * watching until ON_INTERNET / ON_NETWORK / timeout, and if a CANNOT_CONNECT
 * was followed by recovery, we record it as a `cannot-connect-recovered`
 * anomaly on the LogEntry. If the attempt times out with CANNOT_CONNECT as
 * the most recent label, that's a real fail (`cannot-connect-stuck`).
 *
 * CONNECTED on its own is also non-terminal — it means associated to the
 * AP but reachability is unconfirmed.
 */
export function deriveResult(
  transitions: readonly Transition[],
  options: DeriveOptions,
): DerivedResult {
  const timeoutMs = options.timeoutMs ?? DEFAULT_ATTEMPT_TIMEOUT_MS;
  const tConnectedMs = firstTimeMs(transitions, 'CONNECTED');
  const tOnInternetMs = firstTimeMs(transitions, 'ON_INTERNET');
  const anomalies = detectAnomalies(transitions);

  // ON_INTERNET wins regardless of what came before. CANNOT_CONNECT-then-
  // ON_INTERNET still passes; the anomaly captures the irregular path.
  if (tOnInternetMs !== null) {
    return {
      result: 'pass',
      failureReason: null,
      totalDurationMs: tOnInternetMs,
      tConnectedMs,
      tOnInternetMs,
      anomalies,
    };
  }

  // ON_NETWORK is terminal-fail (already provisioned, not our test).
  for (const t of transitions) {
    if (t.label === 'ON_NETWORK') {
      return {
        result: 'fail',
        failureReason:
          `Reached ON_NETWORK after ${t.tMs}ms — device is already provisioned ` +
          `to an owner. Not a passing test; pick a different unit.`,
        totalDurationMs: t.tMs,
        tConnectedMs,
        tOnInternetMs,
        anomalies,
      };
    }
  }

  const elapsed = Math.max(0, options.nowMs - options.startedAtMs);
  if (elapsed >= timeoutMs) {
    const lastLabel = transitions[transitions.length - 1]?.label ?? 'UNKNOWN';
    let failureReason: string;
    let result: AttemptResult;
    if (lastLabel === 'CANNOT_CONNECT') {
      result = 'fail';
      failureReason =
        `Stalled at CANNOT_CONNECT for the full ${Math.round(elapsed / 1000)}s ` +
        `attempt window — wrong password, wrong SSID, out of range, or AP ` +
        `rejecting the client.`;
    } else if (lastLabel === 'CONNECTED') {
      result = 'timeout';
      failureReason =
        `Timed out after ${elapsed}ms — associated to AP at ` +
        `${tConnectedMs}ms but never reached ON_INTERNET (no ping ` +
        `confirmation through the status endpoint).`;
    } else if (tConnectedMs !== null) {
      // Reached CONNECTED at some point but the most recent label is
      // something else (e.g., back to NOT_CONNECTED). Treat as timeout
      // since association did happen briefly.
      result = 'timeout';
      failureReason =
        `Timed out after ${elapsed}ms — last status ${lastLabel}; ` +
        `associated briefly at ${tConnectedMs}ms but did not stay ` +
        `connected through to ON_INTERNET.`;
    } else {
      result = 'fail';
      failureReason =
        `Failed to associate within ${Math.round(elapsed / 1000)}s. ` +
        `Last status ${lastLabel}; never reached CONNECTED. Likely wrong ` +
        `password, wrong SSID, out of range, or AP rejecting the client.`;
    }
    return {
      result,
      failureReason,
      totalDurationMs: elapsed,
      tConnectedMs,
      tOnInternetMs,
      anomalies,
    };
  }

  return {
    result: 'pending',
    failureReason: null,
    totalDurationMs: elapsed,
    tConnectedMs,
    tOnInternetMs,
    anomalies,
  };
}

function firstTimeMs(
  transitions: readonly Transition[],
  label: Transition['label'],
): number | null {
  for (const t of transitions) {
    if (t.label === label) return t.tMs;
  }
  return null;
}

/**
 * Detect unexpected firmware behaviors. The harness only writes during the
 * initial setup sequence (ssid → password → pingEndpoint → security →
 * connect-trigger) and is silent thereafter, so any post-trigger transition
 * is the firmware's own doing.
 *
 * Two patterns today; the union is open so future bring-up rounds can add
 * more without breaking stored logs.
 *
 *  - `cannot-connect-recovered` — CANNOT_CONNECT followed by CONNECTED /
 *    ON_INTERNET without the harness re-issuing any writes.
 *  - `not-configured-after-write` — the device reverts to NOT_CONFIGURED
 *    after having already accepted its configuration (observed
 *    intermittently on 2026-05-07 hardware runs).
 */
function detectAnomalies(
  transitions: readonly Transition[],
): readonly AttemptAnomaly[] {
  const out: AttemptAnomaly[] = [];

  for (let i = 0; i < transitions.length - 1; i += 1) {
    const t = transitions[i]!;
    if (t.label !== 'CANNOT_CONNECT') continue;
    // Find the best post-CANNOT_CONNECT recovery state in the *remainder*
    // of the trace. ON_INTERNET is the strongest evidence that the device
    // ultimately succeeded; fall back to CONNECTED if association happened
    // but the ping endpoint was never reached.
    let onInternet: Transition | null = null;
    let connected: Transition | null = null;
    for (let j = i + 1; j < transitions.length; j += 1) {
      const next = transitions[j]!;
      if (next.label === 'ON_INTERNET' && onInternet === null) onInternet = next;
      if (next.label === 'CONNECTED' && connected === null) connected = next;
    }
    const recovered = onInternet ?? connected;
    if (!recovered) continue;
    out.push({
      kind: 'cannot-connect-recovered',
      message:
        `Device emitted CANNOT_CONNECT at ${t.tMs}ms but subsequently ` +
        `reached ${recovered.label} at ${recovered.tMs}ms without the ` +
        `harness re-issuing any writes. SPEC defines CANNOT_CONNECT as ` +
        `"Attempted and failed"; firmware should not advertise it as a ` +
        `transient state during association retries. Captured for review.`,
      tMs: t.tMs,
    });
  }

  // NOT_CONFIGURED arriving after the device had already accepted its config
  // (any label other than UNKNOWN / NOT_CONFIGURED) — the firmware dropped a
  // configuration the harness never un-set. UNKNOWN → NOT_CONFIGURED early in
  // the trace is benign progression and is intentionally not flagged.
  for (let i = 1; i < transitions.length; i += 1) {
    const t = transitions[i]!;
    if (t.label !== 'NOT_CONFIGURED') continue;
    const accepted = transitions
      .slice(0, i)
      .find((p) => p.label !== 'UNKNOWN' && p.label !== 'NOT_CONFIGURED');
    if (!accepted) continue;
    out.push({
      kind: 'not-configured-after-write',
      message:
        `Device reverted to NOT_CONFIGURED at ${t.tMs}ms after already ` +
        `reporting ${accepted.label} at ${accepted.tMs}ms — the firmware ` +
        `dropped a configuration the harness never un-set. Observed ` +
        `intermittently; captured for review.`,
      tMs: t.tMs,
    });
  }

  return out;
}
